import {
  ICompanyCultureValuePure,
  ICompanyDepartmentPure,
  ICompanyJobPure,
  ICompanyCultureRankPure,
  ICompanyLanguageConfig,
  IPdfDisplaySettings,
  IPdfFont,
  JobPropertyInputType,
  IEvaluationScoreOption,
} from './Company'

// The pages that make up a company's public Employer Branding site. Extensible:
// future pages (vacancies, library) get added here.
export enum EbPage {
  Home = 'home',
  Culture = 'culture',
  Careers = 'careers',
  Library = 'library',
}

// Password protection for the public EB site: one site-wide password guards every
// page flagged in `pages`. undefined => nothing is protected.
export interface IPageProtection {
  // Stored readable on purpose: a shared access code the admin must be able to
  // see. NEVER leaves the server, except to a verified admin in edit mode.
  password?: string
  pages?: Partial<Record<EbPage, boolean>>
}

// What a visitor without access gets for a protected page: only the site chrome
// (branding, nav, language) needed to render the password gate, NEVER content.
export interface IPublicLockedPage {
  locked: true
  shared: boolean
  publicUrl: string
  primaryHost?: string
  companyName: string
  contact?: ICompanyLiveManifestoContact
  logo: string
  companyColors: { [key: string]: number[] }
  pageVisibility?: Partial<Record<EbPage, boolean>>
  protectedPages?: Partial<Record<EbPage, boolean>>
  bodyFont?: IPdfFont
  headingFont?: IPdfFont
  languageConfig?: ICompanyLanguageConfig
}

// What the public careers page may export, and the styling/scale data its
// client-side PDF render needs. Built from settings.pdf.permissions; omitted
// entirely when both public flags are off. The job-profile PDF uses only data
// already shown on the page, so `jobProfile` is purely an availability flag;
// the score scales are included only when `evaluationForm` is on.
export interface IPublicExportConfig {
  jobProfile: boolean
  // Availability flag for the public job-profile Word (DOCX) download.
  jobProfileDocx: boolean
  evaluationForm: boolean
  display?: IPdfDisplaySettings
  // Word-specific visibility for the public job-profile Word export.
  docxDisplay?: IPdfDisplaySettings
  evaluationDisplay?: IPdfDisplaySettings
  bodyFont?: IPdfFont
  headingFont?: IPdfFont
  skillScale?: IEvaluationScoreOption[]
  cultureScale?: IEvaluationScoreOption[]
}

// Public, whitelisted payload for a company's Careers page. Derived from the
// company's own jobs — NEVER includes members or any private member data.
export interface IPublicCareersData {
  companyName: string
  contact?: ICompanyLiveManifestoContact
  logo: string
  companyColors: { [key: string]: number[] }
  pageVisibility?: Partial<Record<EbPage, boolean>>
  protectedPages?: Partial<Record<EbPage, boolean>>
  careers?: ICompanyLiveManifestoCareers
  departments: ICompanyDepartmentPure[]
  jobs: ICompanyJobPure[]
  cultureRanks: ICompanyCultureRankPure[]
  exports?: IPublicExportConfig
  // Brand fonts (from company.settings.pdf) so the public page renders its
  // typography in them. Kept top-level (not inside `exports`) so the fonts apply
  // independently of whether public PDF downloads are enabled.
  bodyFont?: IPdfFont
  headingFont?: IPdfFont
  // When true, job cards and the job-profile layover omit decorative system
  // icons for a quieter careers page. undefined => icons shown.
  reduceIconUsage?: boolean
}

export interface IPublicJobProfileSkill {
  _id: string
  name: string
  tag?: string
  description?: string
  descriptionShort?: string
}

export interface IPublicJobProfileCultureValue {
  _id: string
  name: string
  subtitle: string
  description?: string
  behaviors?: string
  // Theme colour key (resolved against the company theme on the public page).
  color: string
}

// A custom job property exposed on the public careers page. Only properties the
// admin flagged `showOnPublic` with a filled-in value reach this list. `type`
// drives rendering (short = inline value, long = markdown block).
export interface IPublicJobProfileProperty {
  label: string
  text: string
  type: JobPropertyInputType
}

// Read-only job-profile detail shown in the public side panel.
export interface IPublicJobProfile {
  jobTitle?: string
  jobLevelTitle: string
  jobLevelDescription?: string
  departmentName?: string
  // Department theming, mirroring the internal export so the public PDF renders an
  // identical header: brand colour as an "hsl(...)" string, header background image
  // URL, and emoji icon. The export path inlines image/icon as data URIs and
  // converts the colour (see publicExport / buildPublicJobProfilePdfData).
  departmentColor?: string
  departmentImage?: string
  departmentIcon?: string
  culturalPhaseTitle?: string
  // Titles of the job levels this one reports to, resolved server-side so no raw
  // ids ever cross the public boundary (mirrors culturalPhaseTitle).
  reportsToTitles?: string[]
  skills: IPublicJobProfileSkill[]
  cultureValues: IPublicJobProfileCultureValue[]
  properties: IPublicJobProfileProperty[]
}

// A single public file shown on the Employer Branding Library page. A whitelisted
// projection of ICompanyFile — NEVER carries internal flags (_id, hiddenFromLibrary,
// publicInLibrary).
export interface IPublicLibraryFile {
  name: string
  url: string
  dateCreated: Date
  coverImage?: string
}

// Public, whitelisted payload for a company's Library page. Only contains files the
// admin explicitly marked public — NEVER members or any private company data.
export interface IPublicLibraryData {
  companyName: string
  contact?: ICompanyLiveManifestoContact
  logo: string
  companyColors: { [key: string]: number[] }
  pageVisibility?: Partial<Record<EbPage, boolean>>
  protectedPages?: Partial<Record<EbPage, boolean>>
  library?: ICompanyLiveManifestoLibrary
  files: IPublicLibraryFile[]
  // Brand fonts (from company.settings.pdf) so the public page renders its
  // typography in them; kept top-level for the same reason as the careers page.
  bodyFont?: IPdfFont
  headingFont?: IPdfFont
}

// One cultural value as shown on the public Culture page: a whitelisted projection
// of ICompanyCultureValuePure plus the image the CMS placed with it. `levels` keeps
// only what hydrateCultureLevels/getCultureTabs need to label the behaviours.
// Just enough of a cultural level for hydrateCultureLevels/getCultureTabs to
// label the behaviours; the rank titles travel separately on the payload.
export interface IPublicCultureLevel {
  cultureRankId: string
  description?: string
}

export interface IPublicCultureValue {
  _id: string
  // The mantra ("From learning to lasting impact").
  name: string
  // The value word itself ("Mission driven").
  subtitle: string
  description: string
  // Theme colour KEY (resolved against companyColors), never a hex.
  color: string
  levels: IPublicCultureLevel[]
  image?: ICompanyLiveManifestoImage
}

// Public, whitelisted payload for a company's Culture page. Built from the
// company's own cultural values — NEVER members or any private company data.
export interface IPublicCultureData {
  companyName: string
  contact?: ICompanyLiveManifestoContact
  logo: string
  companyColors: { [key: string]: number[] }
  pageVisibility?: Partial<Record<EbPage, boolean>>
  protectedPages?: Partial<Record<EbPage, boolean>>
  culture?: ICompanyLiveManifestoCulture
  values: IPublicCultureValue[]
  // The company-wide cultural levels, needed to label each value's behaviours.
  cultureRanks: ICompanyCultureRankPure[]
  // Brand fonts (from company.settings.pdf), top-level for the same reason as the
  // careers and library pages.
  bodyFont?: IPdfFont
  headingFont?: IPdfFont
}

// Domains the public EB site is served on, as `<slug>.<host>`. The first one is
// the default primary domain.
export const BRANDING_HOSTS = [
  'cultural-dna.nl',
  'cultural-dna.co',
  'cultureel-dna.nl',
]

// Each section of the page is a property
export interface ICompanyLiveManifestoPure {
  _id?: any
  companyId: string
  shared: boolean
  publicUrl: string
  // The branding domain the public site lives on; the other BRANDING_HOSTS
  // redirect to it. undefined => BRANDING_HOSTS[0].
  primaryHost?: string
  logo: string
  darkMode: boolean
  lastEdit?: Date
  header: ICompanyLiveManifestoTextBlockImage
  introduction: ICompanyLiveManifestoTextBlockImage
  mission: ICompanyLiveManifestoMission
  cultureValues: ICompanyLiveManifestoCultureValues
  imageGallery: ICompanyLiveManifestoImageGallery
  footer: ICompanyLiveManifestoFooter
  seo: ICompanyLiveManifestoSEO
  // Per-page visibility; undefined => visible (read-time default).
  pageVisibility?: Partial<Record<EbPage, boolean>>
  // Password protection; only present on the admin edit-mode read.
  pageProtection?: IPageProtection
  // Editable header for the derived Careers page.
  careers?: ICompanyLiveManifestoCareers
  // Editable header + per-value imagery for the derived Culture page.
  culture?: ICompanyLiveManifestoCulture
  // Editable header for the Library page.
  library?: ICompanyLiveManifestoLibrary
  // Contact details for the site-wide footer.
  contact?: ICompanyLiveManifestoContact
  // Hides decorative system icons on the public job cards and job-profile
  // layover; undefined => icons shown.
  reduceIconUsage?: boolean
  // Company language preference, injected into the public manifesto payload (not
  // stored on the doc) so external visitors see the page in the company's
  // language and custom terminology (applied by the EB shell).
  languageConfig?: ICompanyLanguageConfig
}
// The front end uses the colors and habits taken from Companies collection
export interface ICompanyLiveManifesto extends ICompanyLiveManifestoPure {
  // Injected from the company doc, like the other public payloads carry it.
  companyName: string
  companyColors: { [key: string]: number[] }
  companyCultureValues: ICompanyCultureValuePure[]
  // Which pages need the password; the public stand-in for pageProtection.
  protectedPages?: Partial<Record<EbPage, boolean>>
  // Brand fonts (from company.settings.pdf) so the public page can render in them.
  bodyFont?: IPdfFont
  headingFont?: IPdfFont
}

export interface ICompanyLiveManifestoTextBlock {
  title?: string
  text?: string
}

export enum ImageOrientation {
  HORIZONTAL = 'horizontal',
  VERTICAL = 'vertical',
  EMPTY_IMAGE = '',
}

export interface ICompanyLiveManifestoImage {
  url?: string
  orientation?: ImageOrientation
  // Copied from the chosen media item when placed; rendered as the public <img> alt.
  altText?: string
}

export interface ICompanyLiveManifestoTextBlockImage {
  textBlock: ICompanyLiveManifestoTextBlock
  image: ICompanyLiveManifestoImage
}

export interface ICompanyLiveManifestoMission {
  textBlock1: ICompanyLiveManifestoTextBlock
  quote: string
  image: ICompanyLiveManifestoImage
  textBlock2: ICompanyLiveManifestoTextBlock
  textBlock3: ICompanyLiveManifestoTextBlock
  textBlock4: ICompanyLiveManifestoTextBlock
}

export interface ICompanyLiveManifestoCultureValues {
  textBlock: ICompanyLiveManifestoTextBlock
}

export interface ICompanyLiveManifestoCareers {
  textBlock: ICompanyLiveManifestoTextBlock
  // Offers the job framework (its own full-width page) from the Careers page;
  // undefined => not offered.
  showFramework?: boolean
  // Adds the radar as a second view on that page. Only honoured together with
  // showFramework; undefined => framework only.
  showRadar?: boolean
  // Lets the reporting relation between jobs leave the server: job profiles,
  // their downloads, the framework and the radar. undefined => kept private.
  showReportsTo?: boolean
}

export interface ICompanyLiveManifestoLibrary {
  textBlock: ICompanyLiveManifestoTextBlock
}

// The image an admin placed with one cultural value on the public Culture page.
// Deliberately stored HERE and not on company.cultureValues: it is page content,
// not part of the value itself, which is used app-wide.
export interface ICompanyLiveManifestoCultureImage {
  cultureValueId: string
  image: ICompanyLiveManifestoImage
}

export interface ICompanyLiveManifestoCulture {
  textBlock: ICompanyLiveManifestoTextBlock
  images?: ICompanyLiveManifestoCultureImage[]
  // Offers the values x levels diagram (its own full-width page) from the Culture
  // page; undefined => not offered.
  showMatrix?: boolean
}

export interface ICompanyLiveManifestoButton {
  text?: string
  url?: string
}

export interface ICompanyLiveManifestoImageGallery {
  image1: ICompanyLiveManifestoImage
  image2: ICompanyLiveManifestoImage
  image3: ICompanyLiveManifestoImage
  image4: ICompanyLiveManifestoImage
  image5: ICompanyLiveManifestoImage
}

export interface ICompanyLiveManifestoFooter {
  textBlock: ICompanyLiveManifestoTextBlock
  button1: ICompanyLiveManifestoButton
  button2: ICompanyLiveManifestoButton
  image: ICompanyLiveManifestoImage
}

// Contact details shown in the site-wide public footer. Editable page content, so
// it lives on the manifesto rather than on the company document.
export interface ICompanyLiveManifestoContact {
  email?: string
  website?: string
}

export interface ICompanyLiveManifestoSEO {
  metaTitle: string
  metaDescription: string
}
